import { Dayparts } from 'core/dayparts/Dayparts';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { LimitationData } from 'core/limitation/Limitation';
import { Pmp } from 'core/pmp/Pmp';
import { SpaceHealthState } from 'enum/SpaceHealthState';
import * as SelectOptionsUtils from 'utils/SelectOptionsUtils';

export type RtbCampaign = {
  basic: RtbCampaignBasic;
  limitations: LimitationData;
};

export type RtbCampaignBasic = {
  id?: number;
  name: string;
  advertiserId: number;
  orderId: number;
  budget: number;
  startDate: string;
  endDate: string;
  createDate?: string;
  priceModel: RtbCampaignPlanType;
  orderPrice?: number;
  optimize: L2ObjectOptimizationGoal;
  bidPrice?: number;
  adminBidPrice?: number;
  creativeDeliverType: CreativeDeliverType;
  deliverType: DeliverType;
  state?: CampaignState;
  spents?: number;
  currencyRate?: number;
  olapActualSpent?: number;
  olapExpectSpent?: number;
  expectedSpent: number;
  impres?: number;
  clicks?: number;
  dailyTargetBudget: number | null;
  frequency?: CampaignFrequency;
  dayPart?: Dayparts;
  checkpoints: CheckPoint[];
  dealIds?: string[];
  enableMonitor?: boolean;
  adType: string;
  adTypeId?: number;
  videoAdViewObjective?: VideoAdViewObjectiveType;
  conversionTracking?: ConversionTracking;
  viewTrackingCodes?: ViewTrackingCode[];
  videoProgressTrackingCode?: VideoProgressTrackingCode;
  tags: Array<string>;
  convTrackEvent?: string;
  orderPriceEnable: boolean;
  isDraft?: boolean;
  draftId?: number;
  outdoorDeliverType?: OutdoorDeliverType;
  goGanGroupId?: number;
  ageMin: number;
  ageMax: number;
  isPmp?: boolean;
  fixOutdoorSpace?: boolean;
  isTestMode?: boolean;
};

export type RtbCampaignFormData = {
  basic: RtbCampaignBasicFormData;
  limitations: LimitationData;
};

export type RtbCampaignBasicFormData = RtbCampaignBasic & {
  messages: string;
  bidStrategy: BidStrategy;
  bindings: {
    bindingId: string;
    active: boolean;
  }[];
};

export type RtbCampaignListBasic = Omit<
  RtbCampaignBasic,
  'orderPriceEnable'
> & {
  effectiveStatus: string;
  viewable: number;
  convs: number;
  adView: number;
  magnificationRatio: number;
  l2ObjectId: number;
  additionalInfo: AdditionalInfo;
  uuCount: number;
  report?: {
    results: number;
  };
  bindings?: {
    bindingId: string;
    active: boolean;
  };
  adminBidPrice?: number;
  deliveryTargetCount: number;
  deliveryTargetChangeCount: number;
  weatherFilteredCount: number;
  simulcastMismatchCount: number;
  // ECOPD-5620: 門市超過鎖定上限。電子紙專屬——這檔活動鎖到的機台今天已經被 10 個
  // 活動選滿，排不進去。非電子紙渠道一律是 0。每多一個 FilterReason 就多一個要從
  // deliveryTargetCount 扣掉的欄位。
  storeOverBindingLimitCount: number;
  // ECOPD-5844: 版位全部已在 RMax 刪除的門市（機台）數，畫面上併進門市異動計算。
  spaceDeletedCount: number;
  // ECOPD-5691: 逐間門市的異常狀態，只含投遞不出去的那些。
  //
  // 🔴 null 代表「這支沒回」（舊後端、或請求沒帶 additional_info=limitations），
  // 空陣列代表「回了，但這檔活動沒有異常門市」。兩者語意不同，判斷時不可以用
  // _.isEmpty 或 ?.length —— 那兩個會把 null 與空陣列混為一談。
  deliveryTargetStatuses: DeliveryTargetStatus[] | null;
  // ECOPD-5619: 這個電子紙活動現在能不能被啟用。
  //
  // 🔴 null 代表「不適用」，不是「可以啟用」：非電子紙渠道、活動已經是啟用狀態、
  // 或請求沒帶 additional_info=limitations（後端那時根本沒算）都會是 null。
  // 當成 true 用會讓所有非電子紙活動的啟用檢查形同不存在，當成 false 用則會把
  // 它們全部擋掉。
  epaperActivatable: boolean | null;
};

export type AdditionalInfo = {
  creativeAmount: CreativeAmount;
  limitations: LimitationData;
  viewTrackingSize?: number;
  conversionTracking?: AdditionalInfoConversionTracking;
  videoProgressTrackingOffset?: number;
  pmp?: Pmp;
  originPmp?: Pmp;
};

export type CreativeAmount = {
  bindingCount: number;
  enableCount: number;
};

export type AdditionalInfoConversionTracking = {
  conversionType: string;
  id: number;
  name: string;
  conversionId: string;
};

export type ConversionTracking = {
  label: string;
  value: string;
};

export enum ViewTrackingType {
  PIXEL = 'pixel',
  JS = 'js'
}

export enum ViewTrackingTrigger {
  VIEWABLE_IMPRESSION = 'viewableImpression',
  IMPRESSION = 'impression'
}

export type ViewTrackingCode = {
  originTrackingCode: string;
  trackingCode: string;
  trackingType: ViewTrackingType;
  trigger: ViewTrackingTrigger;
};

export type VideoProgressTrackingCode = {
  offset: number;
  code: string;
};

export type CampaignFrequency = {
  maxFrequency: number;
  intervalDays: number;
};

export type CheckPoint = {
  target: number;
  time: string;
  utime: string;
};

export type VideoAdViewObjectiveType =
  | {
      videoAdEvent: VideoAdViewObjective.DEFAULT;
    }
  | {
      videoAdEvent: VideoAdViewObjective.PROGRESS;
      offset: number;
    }
  | {
      videoAdEvent: VideoAdViewObjective.METRIC;
      videoAdMetricEvent: VideoAdMetricEvent;
    };

export enum AdType {
  DISPLAY = 'DISPLAY',
  VIDEO = 'VIDEO',
  COMBO = 'COMBO',
  THIRD_PARTY = 'THIRD_PARTY',
  THIRD_PARTY_BOTTOM = 'THIRD_PARTY_BOTTOM',
  THIRD_PARTY_RECTANGLE = 'THIRD_PARTY_RECTANGLE',
  UNKNOW = 'UNKNOW',
  ONE_FOR_ALL_DISPLAY = 'ONE_FOR_ALL_DISPLAY',
  CTV = 'CTV',
  MESSAGE = 'MESSAGE',
  SANDBOX_OUTDOOR = 'SANDBOX_OUTDOOR',
  OUTDOOR_LINE_MESSAGE = 'OUTDOOR_LINE_MESSAGE',
  SANDBOX_MOVABLE_OUTDOOR = 'SANDBOX_MOVABLE_OUTDOOR',
  LINE_MESSAGE = 'LINE_MESSAGE'
}

/**
 * 判斷是否為已下架、只能唯讀的 One For All 廣告活動。
 * @param adType 廣告活動類型
 * @returns 是 One For All 展示型廣告時為 true
 */
export const isOneForAllAdType = (adType?: string) =>
  adType === AdType.ONE_FOR_ALL_DISPLAY;

export enum RtbLegacyOptimize {
  CPC = 'CPC',
  CPV = 'CPV',
  CPM = 'CPM',
  VCPM = 'vCPM'
}

export enum RtbCampaignPlanType {
  RS = 'rs',
  FCPC = 'fcpc',
  FCPV = 'fcpv',
  FCPM = 'fcpm',
  FVCPM = 'fvcpm',
  FACPM = 'facpm',
  FWCPM = 'fwcpm',
  DCPM = 'dcpm',
  FBCPM = 'fbcpm'
  // RB = 'rb'
}

export enum CreativeDeliverType {
  OPTIMIZE = 1,
  AVERAGE = 2
}

export enum OutdoorDeliverType {
  OPTIMIZE = 1,
  AVERAGE = 2
}

export enum CampaignState {
  ACTIVATE = 1,
  DEACTIVATE = 2,
  DELETE = 3,
  STOPPING = 4
}

export enum DailyBudgetPlan {
  SCHEDULE,
  DAILY
}

export enum VideoAdViewObjective {
  DEFAULT = 'defualt',
  PROGRESS = 'progress',
  METRIC = 'metric'
}

export enum VideoAdMetricEvent {
  FIRSTQUARTILE = 'firstQuartile',
  MIDPOINT = 'midpoint',
  // THIRDQUARTILE = 'thirdQuartile',
  COMPLETE = 'complete'
}

export enum DeliverType {
  STANDARD = 1,
  ACCELERATED = 2
}

export enum TrackEvent {
  CLICK = 'click',
  VIEW = 'view'
}

export const RTBCAMPAIGN_DEFAULT_AGE_MIN = 13;
export const RTBCAMPAIGN_DEFAULT_AGE_MAX = 65;

export type SpaceDevice = {
  spaceId: string;
  // 固定門市：store/device 模式會把 spaceId remap 成 municipality/storeNum(/device)
  // 以便聚合，這裡保留 remap 前的真實版位 id，供固定門市對出單台狀態使用。
  rawSpaceId?: string;
  spaceName: string;
  spaceStatus: string;
  storeNum: string;
  locationName: string;
  deviceCharacteristic: string;
  // deviceIds: string[];
  region: string;
  municipality: string;
  district: string;
  address: string;
  longitude: number;
  latitude: number;
  // publisherDomain: string;
  // publisherId: string;
  outdoorImageFile: string[];
  outdoorPlaceCategories: {
    [key: string]: string[];
  };
  // Operational status (ECOPD-5255): reduced from `healthStates` array on the
  // spaces-detail API to the most severe state.
  healthState?: SpaceHealthState;
  // Reason text for banned/abnormal spaces (`spaceStatusModifyReason`).
  spaceStatusModifyReason?: string;
  // ISO timestamp of the latest status sync (`lastSyncedAt`), for the tooltip footer.
  lastSyncedAt?: string;
  // false = 版位已在 RMax 刪除。與 spaceStatus / healthState 無關，不要互相代入。
  active?: boolean;
};

export const conversionTrackingTypeOptions =
  SelectOptionsUtils.createSelectOptionsFromEnum(
    TrackEvent,
    'campaign.labels.convTrackEvent'
  );

// 宣告順序就是後端的去重優先序，前後端必須一致。
export enum FilterReason {
  SPACE_DELETED = 'SPACE_DELETED',
  DELIVERY_TARGET_CHANGE = 'DELIVERY_TARGET_CHANGE',
  WEATHER = 'WEATHER',
  SIMULCAST_MISMATCH = 'SIMULCAST_MISMATCH',
  // ECOPD-5620: 門市超過鎖定上限（電子紙一台機台最多 10 檔活動）。
  STORE_OVER_BINDING_LIMIT = 'STORE_OVER_BINDING_LIMIT'
}

export type DeliveryTarget = {
  deliveryTargetValue: string;
  deliveryTargetName: string;
};

/**
 * 一間投遞不出去的門市（機台）。可投的門市不在陣列裡。
 *
 * `reasons` 已由後端依它自己的去重優先序排好，**至少一個**。`reasons[0]` 就是
 * 這間門市被算進哪一個統計卡數字，前端直接照它取色 —— 自己再排一次會與後端的
 * 去重結果漂移，而且漂移了畫面上看不出來。
 */
export type DeliveryTargetStatus = {
  // 與門市選項的 value 同格式（`城市/門市編號[/機台特徵]`），前端靠它貼回清單。
  deliveryTargetValue: string;
  // `城市_門市編號_門市店名`（機台再黏一段特徵），與選項 label 同格式。
  // 門市的版位全部停用之後就從選項樹消失，那時只能靠這個補回店名；
  // 撈不到名字的合成條目會是 null。
  deliveryTargetName: string | null;
  reasons: FilterReason[];
};

export type SpaceDeliverabilityData = {
  deliverable: DeliveryTarget[];
  // Partial 而不是 Record：後端只回它算過的原因，型別上宣告成每個 key 都有會讓
  // 取用端少寫 fallback，執行時就會拿到 undefined 再 .length 炸掉（ECOPD-5620）。
  nonDeliverableByReason: Partial<Record<FilterReason, DeliveryTarget[]>>;
};
